// Ниши — the `verticals` rows and their `vertical_check_texts` (plan §2.1, §4.1).
//
// Writes go straight to the tables under RLS, like every other admin hook: the owner is `admin`,
// and the grants exist. A save writes the whole editable row with `update … eq('key')` so what
// the owner sees in the editor is exactly what the pipeline reads next night.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isConfigured, isDemoMode, supabase } from '../../../lib/supabase.js';
import { MOCK_CHECK_TEXTS, MOCK_VERTICALS } from '../../../lib/mockData-niche.js';

export interface DiscoveryQuestion {
  when: string;
  text_bg: string;
}

export interface VerticalRow {
  key: string;
  name_bg: string;
  name_en: string;
  active: boolean;
  origin: string;
  audience_noun_bg: string;
  pitch_style_bg: string | null;
  places_included_types: string[];
  name_include_kw: string[];
  name_exclude_kw: string[];
  synonyms_bg: string[];
  synonyms_lat: string[];
  services_bg: string[];
  services_lat: string[];
  search_languages: string[];
  intent_phrasings_bg: string[];
  osm_tags: string[];
  booking_hints: string[];
  grid_sweep: boolean;
  discovery_share: number;
  check_importance: Record<string, string>;
  discovery_questions: DiscoveryQuestion[];
  call_dead_hours: Record<string, string>;
}

/** Everything the editor may change; `key` and `origin` are identity, not settings. */
export type VerticalPatch = Omit<VerticalRow, 'key' | 'origin'>;

/** How an editor tab writes one field of the draft. */
export type SetVerticalField = <K extends keyof VerticalPatch>(
  key: K,
  value: VerticalPatch[K],
) => void;

export interface CheckTextRow {
  id: number | null;
  vertical_key: string;
  check_key: string;
  title_bg: string;
  ok_bg: string | null;
  missing_bg: string | null;
  improvement_bg: string | null;
  consequence_bg: string | null;
}

export interface VerticalCounts {
  queries: number;
  leads: number;
}

export const VERTICAL_COLUMNS =
  'key, name_bg, name_en, active, origin, audience_noun_bg, pitch_style_bg,' +
  ' places_included_types, name_include_kw, name_exclude_kw, synonyms_bg, synonyms_lat,' +
  ' services_bg, services_lat, search_languages, intent_phrasings_bg, osm_tags, booking_hints,' +
  ' grid_sweep, discovery_share, check_importance, discovery_questions, call_dead_hours';

export function useVerticals() {
  return useQuery({
    queryKey: ['verticals'],
    queryFn: async (): Promise<VerticalRow[]> => {
      if (!isConfigured || isDemoMode) return MOCK_VERTICALS;
      const { data, error } = await supabase
        .from('verticals')
        .select(VERTICAL_COLUMNS)
        .order('active', { ascending: false })
        .order('name_bg');
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as VerticalRow[];
    },
  });
}

/** Query and lead counts per vertical: one HEAD request per table per vertical, cheap at this size. */
export function useVerticalCounts(keys: readonly string[]) {
  return useQuery({
    queryKey: ['vertical_counts', [...keys].sort().join(',')],
    enabled: keys.length > 0,
    queryFn: async (): Promise<Record<string, VerticalCounts>> => {
      const out: Record<string, VerticalCounts> = {};
      if (!isConfigured || isDemoMode) {
        for (const key of keys)
          out[key] = key === 'dentist' ? { queries: 35, leads: 124 } : { queries: 0, leads: 0 };
        return out;
      }
      const count = (table: string, key: string) =>
        supabase.from(table).select('id', { count: 'exact', head: true }).eq('vertical_key', key);
      await Promise.all(
        keys.map(async (key) => {
          const [queries, leads] = await Promise.all([
            count('search_queries', key),
            count('leads', key),
          ]);
          if (queries.error) throw new Error(queries.error.message);
          if (leads.error) throw new Error(leads.error.message);
          out[key] = { queries: queries.count ?? 0, leads: leads.count ?? 0 };
        }),
      );
      return out;
    },
  });
}

function invalidateVerticals(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['verticals'] });
  void client.invalidateQueries({ queryKey: ['vertical_counts'] });
  void client.invalidateQueries({ queryKey: ['niche_context'] });
}

export function useUpdateVertical() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, patch }: { key: string; patch: Partial<VerticalPatch> }) => {
      if (!isConfigured || isDemoMode) {
        const row = MOCK_VERTICALS.find((v) => v.key === key);
        if (row) Object.assign(row, patch);
        return;
      }
      const { error } = await supabase.from('verticals').update(patch).eq('key', key);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateVerticals(client),
  });
}

export function useCreateVertical() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      key,
      name_bg,
      name_en,
    }: {
      key: string;
      name_bg: string;
      name_en: string;
    }) => {
      if (!isConfigured || isDemoMode) {
        const template = MOCK_VERTICALS[0];
        if (template)
          MOCK_VERTICALS.push({
            ...template,
            key,
            name_bg,
            name_en,
            active: false,
            origin: 'manual',
          });
        return;
      }
      const { error } = await supabase
        .from('verticals')
        .insert({ key, name_bg, name_en, active: false, origin: 'manual' });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateVerticals(client),
  });
}

export function useCheckTexts(verticalKey: string | null) {
  return useQuery({
    queryKey: ['vertical_check_texts', verticalKey],
    enabled: verticalKey !== null,
    queryFn: async (): Promise<CheckTextRow[]> => {
      if (!isConfigured || isDemoMode) {
        return MOCK_CHECK_TEXTS.filter((t) => t.vertical_key === verticalKey);
      }
      const { data, error } = await supabase
        .from('vertical_check_texts')
        .select(
          'id, vertical_key, check_key, title_bg, ok_bg, missing_bg, improvement_bg, consequence_bg',
        )
        .eq('vertical_key', verticalKey!)
        .order('check_key');
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as CheckTextRow[];
    },
  });
}

/** Upsert on (vertical_key, check_key): new rows insert, existing rows update. */
export function useSaveCheckTexts() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (rows: readonly CheckTextRow[]) => {
      if (rows.length === 0) return;
      if (!isConfigured || isDemoMode) {
        for (const row of rows) {
          const idx = MOCK_CHECK_TEXTS.findIndex(
            (t) => t.vertical_key === row.vertical_key && t.check_key === row.check_key,
          );
          if (idx === -1) MOCK_CHECK_TEXTS.push({ ...row, id: Date.now() });
          else MOCK_CHECK_TEXTS[idx] = { ...row, id: MOCK_CHECK_TEXTS[idx]?.id ?? null };
        }
        return;
      }
      const payload = rows.map(({ id: _id, ...rest }) => rest);
      const { error } = await supabase
        .from('vertical_check_texts')
        .upsert(payload, { onConflict: 'vertical_key,check_key' });
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, rows) =>
      void client.invalidateQueries({ queryKey: ['vertical_check_texts', rows[0]?.vertical_key] }),
  });
}
